import { useEffect, useMemo, useState } from 'react'

import './_privacySettings.scss'
import { useTranslation } from 'react-i18next'
import { InView } from 'react-intersection-observer'
import { useInfiniteQuery } from 'react-query'
import { useLocation } from 'react-router-dom'
import BeatLoader from 'react-spinners/BeatLoader'

import UserCardRestricted from 'components/UI/UserCard/UserCardRestricted'
import LayoutHeader from 'features/LayoutHeader/LayoutHeader'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import { getRestrictedUsers } from 'services/endpoints/settings/account/privacyAndPolicy'

const useSortSubsTop = () => {
    const { t } = useTranslation()

    return useMemo(
        () => [
            {
                value: 'name',
                name: t('name')
            },
            {
                value: 'recently_restricted',
                name: t('recentlyRestricted')
            }
        ],
        [t]
    )
}

const useSubsOrder = () => {
    const { t } = useTranslation()

    return useMemo(
        () => [
            {
                value: 'asc',
                name: t('ascending')
            },
            {
                value: 'desc',
                name: t('descending')
            }
        ],
        [t]
    )
}

const useFilterQuery = () => {
    const { search } = useLocation()

    return useMemo(() => new URLSearchParams(search), [search])
}

export const RestrictedUsers = () => {
    const { t } = useTranslation()

    const [searchTerm, setSearchTerm] = useState('')
    const [selectedSort, setSelectedSort] = useState('name')
    const [selectedOrder, setSelectedOrder] = useState('asc')

    const sortSubsTop = useSortSubsTop()
    const sortSubsOrder = useSubsOrder()

    const filter = useFilterQuery()
    const sort = filter.get('sort')
    const order = filter.get('order')

    useEffect(() => {
        if (sort) {
            setSelectedSort(sort)
        }

        if (order) {
            setSelectedOrder(order)
        }
    }, [sort, order])

    const {
        data,
        error,
        isLoading,
        isFetching,
        isFetchingNextPage,
        fetchNextPage,
        hasNextPage
    } = useInfiniteQuery<any, Error>(
        ['getRestrictedUsers', selectedOrder, selectedSort],
        ({ pageParam = 1 }) =>
            getRestrictedUsers(selectedSort, selectedOrder, pageParam),
        {
            getNextPageParam: lastPage => lastPage.nextCursor,
            refetchOnWindowFocus: false,
            keepPreviousData: true
        }
    )

    const restrictedUsers = useMemo(() => {
        return (
            data?.pages.flatMap(page => {
                return page?.page?.data?.data ?? []
            }) ?? []
        )
    }, [data])

    const filteredRestrictedUsers = useMemo(() => {
        const normalizedSearch = searchTerm.trim().toLowerCase()

        if (!normalizedSearch) {
            return restrictedUsers
        }

        return restrictedUsers.filter(user => {
            const name =
                user?.display_name ??
                user?.name ??
                user?.username ??
                ''

            return name.toLowerCase().includes(normalizedSearch)
        })
    }, [restrictedUsers, searchTerm])

    return (
        <WithHeaderSection
            headerSection={
                <LayoutHeader
                    type='search-with-buttons'
                    searchValue={searchTerm}
                    searchFn={(value: string) => {
                        setSearchTerm(value)
                    }}
                    clearFn={() => {
                        setSearchTerm('')
                    }}
                    additionalProps={{
                        placeholder: t('searchRestrictedUsers')
                    }}
                    buttons={[
                        {
                            type: 'sort',
                            elements: {
                                first_section: sortSubsTop,
                                second_section: sortSubsOrder
                            }
                        }
                    ]}
                    applyFn={(sortValue: string, orderValue?: string) => {
                        if (sortValue) {
                            setSelectedSort(sortValue)
                        }

                        if (orderValue) {
                            setSelectedOrder(orderValue)
                        }
                    }}
                />
            }
        >
            <h3 className='blockedUsers__h3'>
                {t('restrictedUsers')}
            </h3>

            <div className='blockedUsers__content'>
                {isLoading && (
                    <div className='blockedUsers__initialLoader'>
                        <BeatLoader size={8} />
                    </div>
                )}

                {!isLoading && error && (
                    <div className='blockedUsers__error'>
                        {error.message}
                    </div>
                )}

                {!isLoading &&
                    !error &&
                    filteredRestrictedUsers.map((user, index) => {
                        const isLastUser =
                            index === filteredRestrictedUsers.length - 1

                        if (isLastUser && hasNextPage) {
                            return (
                                <InView
                                    key={user.id}
                                    as='div'
                                    threshold={0.1}
                                    rootMargin='150px'
                                    onChange={inView => {
                                        if (
                                            inView &&
                                            !isFetchingNextPage &&
                                            hasNextPage
                                        ) {
                                            fetchNextPage()
                                        }
                                    }}
                                >
                                    <UserCardRestricted user={user} />
                                </InView>
                            )
                        }

                        return (
                            <UserCardRestricted
                                key={user.id}
                                user={user}
                            />
                        )
                    })}

                {!isLoading &&
                    !error &&
                    filteredRestrictedUsers.length === 0 && (
                        <div className='blockedUsers__empty'>
                            {searchTerm
                                ? t('noSearchResults')
                                : t('noRestrictedUsers')}
                        </div>
                    )}

                {isFetchingNextPage && (
                    <div className='blockedUsers__nextPageLoader'>
                        <BeatLoader size={8} />
                    </div>
                )}

                {isFetching &&
                    !isLoading &&
                    !isFetchingNextPage && (
                        <div className='blockedUsers__refreshing' />
                    )}
            </div>
        </WithHeaderSection>
    )
}